import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Building2, Palette, ClipboardList, Handshake, HardHat, Coins, Building } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ConsultationForm } from '../components/LeadForms';
import WireframeBuilding from '../components/WireframeBuilding';

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] }
};

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative h-screen w-full flex items-center justify-center overflow-hidden" data-testid="hero-section">
      {/* Animated Background */}
      <WireframeBuilding className="opacity-40" />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-background/60 to-background" />
      
      {/* Content */}
      <div className="relative z-10 arch-container text-center">
        <motion.div
          variants={stagger}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {/* Company Name - Big and Bold */}
          <motion.div
            variants={fadeUp}
            className="mb-4"
          >
            <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-raleway font-bold text-white tracking-[0.03em] uppercase leading-none">
              AGASTYA
            </h1>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-raleway font-semibold text-brand-beige tracking-[0.3em] uppercase mt-2">
              VENTURES
            </h2>
          </motion.div>
          
          {/* Tagline */}
          <motion.p
            variants={fadeUp}
            className="text-fluid-hero-sub uppercase tracking-[0.25em] text-brand-brown font-medium mt-[120px] mb-[48px]"
          >
            Your Vision, Our Expertise, Exceptional Results
          </motion.p>
          
          {/* Divider */}
          <motion.div
            variants={fadeUp}
            className="flex items-center justify-center gap-4 py-4"
          >
            <div className="h-px w-16 bg-brand-brown/50" />
            <div className="w-2 h-2 bg-brand-brown rotate-45" />
            <div className="h-px w-16 bg-brand-brown/50" />
          </motion.div>
          
          {/* Description */}
          <motion.p
            variants={fadeUp}
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            Premier architecture, interior design, and project management consulting 
            for Mumbai's most discerning real estate developments.
          </motion.p>
          
          {/* CTA Buttons */}
          <motion.div
            variants={fadeUp}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-6"
          >
            <Link to="/contact">
              <Button
                className="bg-brand-brown text-white hover:bg-brand-beige hover:text-black rounded-none px-8 py-6 text-sm uppercase tracking-widest font-bold transition-all duration-300"
                data-testid="hero-cta-primary"
              >
                Schedule Consultation
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/portfolio">
              <Button
                variant="outline"
                className="border-brand-brown/50 text-white hover:bg-brand-brown hover:text-white rounded-none px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300"
                data-testid="hero-cta-secondary"
              >
                View Our Work
              </Button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border border-brand-brown/50 rounded-full flex items-start justify-center p-2"
        >
          <motion.div className="w-1 h-2 bg-brand-brown rounded-full" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// Services Preview Section
const ServicesPreview = () => {
  const services = [
    { icon: Building2, title: 'Architectural Design', desc: 'Comprehensive design services from concept to construction' },
    { icon: Palette, title: 'Interior Design', desc: 'Creating spaces that complement architecture with comfort and style' },
    { icon: ClipboardList, title: 'PMC Services', desc: 'Expert project management ensuring timely delivery' },
    { icon: Handshake, title: 'Liaisoning', desc: 'Securing approvals from regulatory bodies and authorities' },
    { icon: HardHat, title: 'Construction', desc: 'Building structures for residential and commercial purposes' },
    { icon: Coins, title: 'Finance Solutions', desc: 'Specialized funding methods for building projects' },
  ];

  return (
    <section className="arch-section bg-background" data-testid="services-preview-section">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row items-start justify-between gap-8 mb-16"
        >
          <div>
            <p className="text-sm uppercase tracking-widest text-gold mb-4">What We Do</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">
              Comprehensive Real Estate<br />Solutions
            </h2>
          </div>
          <Link to="/services" className="flex-shrink-0">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white hover:text-black rounded-none px-6 py-5 text-xs uppercase tracking-widest"
              data-testid="services-view-all-btn"
            >
              View All Services
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group p-8 md:p-12 bg-background hover:bg-card transition-all duration-500 cursor-pointer"
              data-testid={`service-card-${index}`}
            >
              <service.icon className="w-8 h-8 text-gold mb-6" strokeWidth={1.5} />
              <h3 className="text-xl font-serif text-white mb-3 group-hover:text-gold transition-colors">
                {service.title}
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {service.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Featured Projects Section
const FeaturedProjects = () => {
  const projects = [
    {
      id: 'sagar-apartments',
      name: 'Sagar Apartments',
      type: 'Uber Luxury Residential',
      location: 'Bandra West',
      area: '73,300 Sq.Ft',
      image: '/projects/sagar/sagar_1.png'
    },
    {
      id: 'everest-chs',
      name: 'Everest CHS',
      type: 'Residential + Commercial',
      location: 'Santacruz West',
      area: '70,000 Sq.Ft',
      image: '/projects/everest/everest_1.png'
    },
    {
      id: 'mahavir-square',
      name: 'Mahavir Square',
      type: 'Interior Design',
      location: 'Thane',
      area: 'Private Residence',
      image: '/projects/mahavir/mahavir1_1.png'
    }
  ];

  return (
    <section className="arch-section bg-card" data-testid="featured-projects-section">
      <div className="arch-container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col lg:flex-row items-start justify-between gap-8 mb-16"
        >
          <div>
            <p className="text-sm uppercase tracking-widest text-gold mb-4">Featured Work</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white">
              Projects That Define<br />Excellence
            </h2>
          </div>
          <Link to="/portfolio" className="flex-shrink-0">
            <Button
              variant="outline"
              className="border-white/20 text-white hover:bg-white hover:text-black rounded-none px-6 py-5 text-xs uppercase tracking-widest"
              data-testid="portfolio-view-all-btn"
            >
              View All Projects
              <ArrowUpRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group relative aspect-[4/5] overflow-hidden project-card border border-white/5"
              data-testid={`featured-project-${project.id}`}
            >
              <img
                src={project.image}
                alt={project.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <p className="text-xs uppercase tracking-widest text-gold mb-2">{project.type}</p>
                <h3 className="text-2xl font-serif text-white mb-2">{project.name}</h3>
                <div className="flex items-center gap-4 text-muted-foreground text-sm">
                  <span>{project.location}</span>
                  <span>•</span>
                  <span>{project.area}</span>
                </div>
              </div>
              <Link
                to={`/portfolio/${project.id}`}
                className="absolute inset-0 z-10"
                aria-label={`View ${project.name}`}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Preview Section
const AboutPreview = () => {
  return (
    <section className="arch-section bg-background" data-testid="about-preview-section">
      <div className="arch-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest text-gold mb-4">About Us</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
              Your Complete<br />Real Estate Partner
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-6">
              Agastya Ventures is your trusted, start-to-end real estate partner, delivering 
              comprehensive solutions across India's residential and commercial markets. We manage 
              the entire property transaction lifecycle under one roof—from project curation, due 
              diligence, and financing support to documentation, registration, and post-sale assistance.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              With deep local market expertise and a relationship-driven approach, we focus on 
              long-term value creation. Our commitment ensures every client enjoys a smooth, 
              transparent, and hassle-free real estate journey.
            </p>
            <Link to="/about">
              <Button
                variant="outline"
                className="border-white/20 text-white hover:bg-white hover:text-black rounded-none px-6 py-5 text-xs uppercase tracking-widest"
                data-testid="about-learn-more-btn"
              >
                Learn More About Us
                <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-[4/5] relative">
              <img
                src="/mumbai-skyline.jpg"
                alt="Mumbai Skyline"
                className="w-full h-full object-cover"
              />
              <div className="absolute -bottom-8 -left-8 bg-gold p-8 hidden lg:block">
                <div className="text-black">
                  <p className="text-4xl font-serif font-medium">10+</p>
                  <p className="text-sm uppercase tracking-widest mt-1">Years Experience</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Redevelopment CTA Section
const RedevelopmentCTA = () => {
  return (
    <section className="arch-section bg-card relative overflow-hidden" data-testid="redevelopment-cta-section">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(212, 175, 55, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(212, 175, 55, 0.1) 1px, transparent 1px)',
          backgroundSize: '50px 50px'
        }} />
      </div>
      
      <div className="arch-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-sm uppercase tracking-widest text-gold mb-4">Redevelopment Experts</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-white mb-6">
              Planning Society<br />Redevelopment?
            </h2>
            <p className="text-muted-foreground text-lg leading-relaxed mb-8">
              Navigate the complex redevelopment process with confidence. Our expert PMC team 
              guides housing societies through every step—from feasibility analysis and developer 
              selection to regulatory compliance and project completion.
            </p>
            
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="border-l-2 border-gold pl-4">
                <p className="text-2xl font-serif text-white">Expert</p>
                <p className="text-muted-foreground text-sm">PMC Guidance</p>
              </div>
              <div className="border-l-2 border-gold pl-4">
                <p className="text-2xl font-serif text-white">79A</p>
                <p className="text-muted-foreground text-sm">Process Expertise</p>
              </div>
              <div className="border-l-2 border-gold pl-4">
                <p className="text-2xl font-serif text-white">100%</p>
                <p className="text-muted-foreground text-sm">Transparency</p>
              </div>
              <div className="border-l-2 border-gold pl-4">
                <p className="text-2xl font-serif text-white">End-to-End</p>
                <p className="text-muted-foreground text-sm">Support</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/knowledge-hub">
                <Button
                  className="bg-gold text-black hover:bg-gold-light rounded-none px-6 py-5 text-xs uppercase tracking-widest font-bold"
                  data-testid="redevelopment-learn-btn"
                >
                  Learn About Redevelopment
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white hover:text-black rounded-none px-6 py-5 text-xs uppercase tracking-widest"
                  data-testid="redevelopment-assessment-btn"
                >
                  Get Free Assessment
                </Button>
              </Link>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <ConsultationForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main Home Page Component
const HomePage = () => {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <ServicesPreview />
      <FeaturedProjects />
      <AboutPreview />
      <RedevelopmentCTA />
    </main>
  );
};

export default HomePage;
